// The two editors a workload's form shares: its configuration as name and
// value rows, and what it may reach as rules the executor applies verbatim
// (02-DESIGN/04-workloads/01-the-workload-model.md § the registration).

import { Button } from "../../ui/components.tsx";
import { MAX_NAMES, validEgressRule, validEnvName, validName } from "../../wire/workloads.ts";

/** Pair is one configuration row: a name and a value. */
export interface Pair {
  key: string;
  value: string;
}

export function PairsEditor({ pairs, onChange }: { pairs: Pair[]; onChange: (p: Pair[]) => void }) {
  return (
    <div className="pairs">
      {pairs.map((p, i) => (
        <div className="row" key={i}>
          <input
            className="input mono"
            style={{ flex: 1, minWidth: "6rem" }}
            placeholder="NAME"
            aria-label="Name"
            value={p.key}
            onChange={(e) => {
              onChange(pairs.map((q, j) => (j === i ? { ...q, key: e.target.value } : q)));
            }}
          />
          <span className="faint">=</span>
          <input
            className="input mono"
            style={{ flex: 2, minWidth: "8rem" }}
            placeholder="value"
            aria-label="Value"
            value={p.value}
            onChange={(e) => {
              onChange(pairs.map((q, j) => (j === i ? { ...q, value: e.target.value } : q)));
            }}
          />
          <Button
            kind="quiet"
            size="sm"
            onClick={() => {
              onChange(pairs.filter((_, j) => j !== i));
            }}
          >
            Remove
          </Button>
        </div>
      ))}
      <div className="row">
        <Button
          size="sm"
          onClick={() => {
            onChange([...pairs, { key: "", value: "" }]);
          }}
        >
          Add a value
        </Button>
      </div>
    </div>
  );
}

export function RulesEditor({ rules, onChange }: { rules: string[]; onChange: (r: string[]) => void }) {
  return (
    <div className="pairs">
      {rules.map((r, i) => (
        <div className="row" key={i}>
          <input
            className="input mono"
            style={{ flex: 1 }}
            placeholder="allow@api.example:tcp:443"
            aria-label="Rule"
            value={r}
            onChange={(e) => {
              onChange(rules.map((q, j) => (j === i ? e.target.value : q)));
            }}
          />
          {r !== "" && !validEgressRule(r) && <span className="small bad">not a rule</span>}
          <Button
            kind="quiet"
            size="sm"
            onClick={() => {
              onChange(rules.filter((_, j) => j !== i));
            }}
          >
            Remove
          </Button>
        </div>
      ))}
      <div className="row">
        <Button
          size="sm"
          onClick={() => {
            onChange([...rules, ""]);
          }}
        >
          Add a rule
        </Button>
      </div>
    </div>
  );
}

/** configurationOf turns the rows into the registration's map, refusing a bad name. */
export function configurationOf(pairs: Pair[]): Record<string, string> | Error {
  const out: Record<string, string> = {};
  for (const p of pairs) {
    const k = p.key.trim();
    if (k === "" && p.value === "") {
      continue;
    }
    if (!validEnvName(k)) {
      return new Error(`"${k}" is not an environment variable name`);
    }
    out[k] = p.value;
  }
  return out;
}

/**
 * namesOf reads the names a workload answers discovery under from one line,
 * split on commas and spaces — unique in the workspace, and none of the
 * platform's (decision 0026).
 */
export function namesOf(line: string): string[] | Error {
  const out: string[] = [];
  for (const n of line.split(/[\s,]+/)) {
    if (n === "") {
      continue;
    }
    if (!validName(n)) {
      return new Error(
        `"${n}" is not a name: letters, digits, dot, dash and underscore, and not one of Impire's own`,
      );
    }
    if (!out.includes(n)) {
      out.push(n);
    }
  }
  if (out.length > MAX_NAMES) {
    return new Error(`at most ${String(MAX_NAMES)} names`);
  }
  return out;
}

/** egressOf trims the rows, refusing a rule the executor would not parse. */
export function egressOf(rules: string[]): string[] | Error {
  const out: string[] = [];
  for (const r of rules) {
    const t = r.trim();
    if (t === "") {
      continue;
    }
    if (!validEgressRule(t)) {
      return new Error(`"${t}" is not a rule: <action>[:<direction>]@<target>[:<proto>[:<ports>]]`);
    }
    out.push(t);
  }
  return out;
}

/**
 * COPIES_HINT says what the count is (decision 0035): copies spread across
 * hosts where the install has several, and a change replaced one at a time.
 */
export const COPIES_HINT =
  "How many copies run at once, each on its own host where the install has several. A change replaces them one at a time while the others keep answering.";

/**
 * copiesOf reads the count a form asks for: a whole number, one or more. The
 * install's bound is the workload service's to refuse, since the console
 * carries no install fact.
 */
export function copiesOf(line: string): number | Error {
  const t = line.trim();
  const n = t === "" ? 1 : Number(t);
  if (!Number.isInteger(n) || n < 1) {
    return new Error(`"${t}" is not a number of copies: a whole number, one or more`);
  }
  return n;
}
